import React, { useCallback, useRef, useState } from 'react'
import {
  Button,
  CheckboxList,
  CheckboxOption,
  Dropdown,
  Input,
  Notification,
  NotificationType,
  Content,
  RadioGroup
} from '@ampcore/adel-react'
import Cookies from 'js-cookie'
import StatesData from '../views/usa-states.json'
import {
  isValidEmail,
  isValidZipcode,
  formatPhoneNumber,
  isValidPhoneNumber
} from '../../../common/utils'
import xss from 'xss'
import { formSelectedData, CONSTs } from '../views/utils'
import { ccpa } from '../views/Constants'
import '../views/index.css'

export type CCPAFieldName =
  | 'resident'
  | 'firstname'
  | 'lastname'
  | 'emailaddress'
  | 'phone'
  | 'street'
  | 'city'
  | 'state'
  | 'zipcode'

export type CCPAFormData = {
  resident: string | null
  relationship: string[]
  requesttype: string[]
  firstname: string | null
  lastname: string | null
  clientid: string
  emailaddress: string | null
  street: string | null
  city: string | null
  state: string | null
  zipcode: string | null
  phone: string | null
  secureSite?: string | null
}

export type CCPAErrorFields = {
  resident: boolean
  relationship: boolean
  requesttype: boolean
  firstname: boolean
  lastname: boolean
  emailaddress: boolean
  state: boolean
  city: boolean
  street: boolean
  zipcode: boolean
  phone: boolean
  emailWrongFormat: boolean
}

export type CCPAFormLabels = {
  firstname: string
  lastname: string
  emailaddress: string
  phone: string
  street: string
  city: string
  state: string
  zipcode: string
  accountid?: string
  employeeid?: string
  buttonlabel: string
  buttonLoadingLabel: string
}

export type CCPAFormErrorMessages = {
  errorResident?: string
  errorRelationship: string
  errorRequesttype: string
  errorFirstname: string
  errorLastname: string
  errorEmailaddress: string
  errorEmailaddressWrongFormat: string
  errorPhone: string
  errorStreet: string
  errorCity: string
  errorState?: string
  errorZipcode: string
}

export type CCPAServiceMessages = {
  serviceSuccess: string
  serviceSuccessThankyou: string
  serviceFailed: string
}

export type CCPAFormHeading = {
  title: string
}

export type CCPAFormPleaseNote = {
  message: string
}

export type CCPAFormEmailInfo = {
  subject: string
  toEmailAddress: string
  emailApi: string
}

export type ContactFormCCPAContent = {
  formEmailInfo?: CCPAFormEmailInfo
  formTemplate?: string
  formErrorMessages: CCPAFormErrorMessages
  formPleaseNote?: CCPAFormPleaseNote
  serviceMessages?: Partial<CCPAServiceMessages>
  formHeading?: CCPAFormHeading
  formLabels: CCPAFormLabels
}

export type CCPAShowServerResponse = (
  props: unknown,
  message?: string
) => boolean

export type CCPACallback = (
  items: unknown,
  formEmailInfo: CCPAFormEmailInfo | undefined,
  showServerResponse: CCPAShowServerResponse
) => unknown

export type ContactFormCCPAProps = {
  callback?: CCPACallback
  name?: string
  content: ContactFormCCPAContent
  testId?: string
}

const relationshipData: CheckboxOption[] = [
  { name: 'Applicant', value: 'Applicant', checked: false },
  { name: 'Employee', value: 'Employee', checked: false },
  { name: 'Client', value: 'Client', checked: false },
  { name: 'ProspectiveClient', value: 'Prospective Client', checked: false },
  { name: 'Other', value: 'Other', checked: false }
]

const requestTypeData: CheckboxOption[] = [
  { name: 'AccessData', value: 'Access Data', checked: false },
  { name: 'DeleteData', value: 'Delete Data', checked: false },
  { name: 'CorrectData', value: 'Correct Data', checked: false }
]

const getInitialClientId = (): string => {
  const userStatus = Cookies.get('lastAmpAuthState') || 'unrecognized'
  return userStatus === 'unrecognized' ? ccpa.clientid.no : ccpa.clientid.yes
}

const ContactFormCCPA = (props: ContactFormCCPAProps) => {
  const { callback, name, content, testId } = props
  const {
    formErrorMessages,
    formPleaseNote,
    serviceMessages,
    formHeading,
    formLabels
  } = content

  const [showServiceFailedMessage, setShowServiceFailedMessage] =
    useState(false)
  const [showSuccessMessage, setShowSuccessMessage] = useState(false)
  const [loading, setLoading] = useState(false)

  const [formData, setFormData] = useState<CCPAFormData>({
    resident: null,
    relationship: [],
    requesttype: [],
    firstname: null,
    lastname: null,
    clientid: getInitialClientId(),
    emailaddress: null,
    street: null,
    city: null,
    state: null,
    zipcode: null,
    phone: null
  })

  const [errorFields, setErrorFields] = useState<CCPAErrorFields>({
    resident: false,
    relationship: false,
    requesttype: false,
    firstname: false,
    lastname: false,
    emailaddress: false,
    state: false,
    city: false,
    street: false,
    zipcode: false,
    phone: false,
    emailWrongFormat: false
  })

  const firstnameRef = useRef<HTMLInputElement | null>(null)
  const lastnameRef = useRef<HTMLInputElement | null>(null)
  const emailaddressRef = useRef<HTMLInputElement | null>(null)
  const phoneRef = useRef<HTMLInputElement | null>(null)
  const streetRef = useRef<HTMLInputElement | null>(null)
  const cityRef = useRef<HTMLInputElement | null>(null)
  const stateRef = useRef<HTMLSelectElement | null>(null)
  const zipcodeRef = useRef<HTMLInputElement | null>(null)
  const relationshipInteracted = useRef(false)
  const requestTypeInteracted = useRef(false)

  const showServerResponse: CCPAShowServerResponse = (props, message) => {
    if (props && message) {
      if (message === CONSTs.SUCCESS) {
        setLoading(false)
        setShowSuccessMessage(true)
        setShowServiceFailedMessage(false)
      } else {
        setLoading(false)
        setShowSuccessMessage(false)
        setShowServiceFailedMessage(true)
      }
    }
    return true
  }

  const getFormStatus = (): boolean => {
    for (const key in formData) {
      const typedKey = key as keyof CCPAFormData
      if (typedKey === 'clientid' || typedKey === 'secureSite') continue
      const value = formData[typedKey]
      if (Array.isArray(value)) {
        if (value.length === 0) return false
      } else if (value === null) {
        return false
      }
    }
    return true
  }

  const validateField = (fieldName: CCPAFieldName, value: string) => {
    const fieldValues: CCPAFormData = { ...formData }
    const fieldErrors: CCPAErrorFields = { ...errorFields }

    switch (fieldName) {
    case 'resident': {
      const isValid = value !== ''
      fieldValues.resident = isValid ? value : null
      fieldErrors.resident = !isValid
      break
    }
    case 'firstname': {
      const isValid = value.length > 0
      fieldValues.firstname = isValid ? value : null
      fieldErrors.firstname = !isValid
      break
    }
    case 'lastname': {
      const isValid = value.length > 0
      fieldValues.lastname = isValid ? value : null
      fieldErrors.lastname = !isValid
      break
    }
    case 'emailaddress': {
      const isEmailValid = isValidEmail(value)
      fieldErrors.emailaddress = !isEmailValid
      fieldErrors.emailWrongFormat = !isEmailValid && value.length > 0
      fieldValues.emailaddress = isEmailValid ? value : null
      break
    }
    case 'phone': {
      const isValidPhone = isValidPhoneNumber(value, true)
      fieldErrors.phone = !isValidPhone
      if (isValidPhone) {
        const formattedPhone = formatPhoneNumber(value)
        if (phoneRef.current) {
          phoneRef.current.value = formattedPhone
        }
        fieldValues.phone = formattedPhone
      } else {
        fieldValues.phone = null
      }
      break
    }
    case 'street': {
      const isValid = value.length > 0
      fieldValues.street = isValid ? value : null
      fieldErrors.street = !isValid
      break
    }
    case 'city': {
      const isValid = value.length > 0
      fieldValues.city = isValid ? value : null
      fieldErrors.city = !isValid
      break
    }
    case 'state': {
      const isValid = value.length > 0
      fieldValues.state = isValid ? value : null
      fieldErrors.state = !isValid
      break
    }
    case 'zipcode': {
      const isValid = isValidZipcode(value)
      fieldValues.zipcode = isValid ? value : null
      fieldErrors.zipcode = !isValid
      break
    }
    }

    setFormData(fieldValues)
    setErrorFields(fieldErrors)
  }

  const handleFieldChange = (event: React.SyntheticEvent<HTMLElement>) => {
    const target = event.target as HTMLInputElement | null
    if (!target || !target.name) return

    const fieldName = target.name as CCPAFieldName
    const value = target.value

    setFormData(prevState => ({ ...prevState, [fieldName]: value || null }))
    validateField(fieldName, value)
  }

  const handleOptionSelected = useCallback((option: string | null) => {
    if (option) {
      setFormData(prevState => ({ ...prevState, resident: option }))
      setErrorFields(prevErrors => ({ ...prevErrors, resident: false }))
    }
  }, [])

  const handleOptionalRadio = useCallback((option: string | null) => {
    if (option) {
      setFormData(prevState => ({ ...prevState, secureSite: option }))
    }
  }, [])

  const handleRelationshipChange = useCallback((options: CheckboxOption[]) => {
    const selected = options.filter(o => o.checked).map(o => o.value)
    if (selected.length > 0) relationshipInteracted.current = true
    setFormData(prevState => ({ ...prevState, relationship: selected }))
    if (relationshipInteracted.current) {
      setErrorFields(prevErrors => ({
        ...prevErrors,
        relationship: selected.length === 0
      }))
    }
  }, [])

  const handleRequestTypeChange = useCallback((options: CheckboxOption[]) => {
    const selected = options.filter(o => o.checked).map(o => o.value)
    if (selected.length > 0) requestTypeInteracted.current = true
    setFormData(prevState => ({ ...prevState, requesttype: selected }))
    if (requestTypeInteracted.current) {
      setErrorFields(prevErrors => ({
        ...prevErrors,
        requesttype: selected.length === 0
      }))
    }
  }, [])

  const handleSelectState = (value: string) => {
    setFormData(prevState => ({ ...prevState, state: value || null }))
    validateField('state', value)
  }

  const submitData = (event: { preventDefault: () => void }) => {
    event.preventDefault()
    const { formEmailInfo, formTemplate } = content

    const canFormBeSubmitted = getFormStatus()

    if (canFormBeSubmitted) {
      setLoading(true)
      const items = formSelectedData(formData, formTemplate)
      if (callback && typeof callback === 'function' && items) {
        return callback(items, formEmailInfo, showServerResponse)
      }
      return true
    }

    const formFirstName = firstnameRef.current?.value || ''
    const formLastName = lastnameRef.current?.value || ''
    const formEmailAddress = emailaddressRef.current?.value || ''
    const formPhoneNumber = phoneRef.current?.value || ''
    const formStreet = streetRef.current?.value || ''
    const formCity = cityRef.current?.value || ''
    const formState = stateRef.current?.value || ''
    const formZipCode = zipcodeRef.current?.value || ''

    const fieldValues: CCPAFormData = { ...formData }
    const fieldErrors: CCPAErrorFields = { ...errorFields }

    // CA resident validation
    if (!formData.resident) {
      fieldValues.resident = null
      fieldErrors.resident = true
    } else {
      fieldErrors.resident = false
    }

    // Relationship validation
    if (!formData.relationship.length) {
      fieldValues.relationship = []
      fieldErrors.relationship = true
    } else {
      fieldErrors.relationship = false
    }

    // Request type validation
    if (!formData.requesttype.length) {
      fieldValues.requesttype = []
      fieldErrors.requesttype = true
    } else {
      fieldErrors.requesttype = false
    }

    // First name validation
    if (formFirstName.length < 1) {
      fieldValues.firstname = null
      fieldErrors.firstname = true
    } else {
      fieldValues.firstname = formData.firstname
      fieldErrors.firstname = false
    }

    // Last name validation
    if (formLastName.length < 1) {
      fieldValues.lastname = null
      fieldErrors.lastname = true
    } else {
      fieldValues.lastname = formData.lastname
      fieldErrors.lastname = false
    }

    // Email validation
    if (formEmailAddress) {
      const cleanedEmail = formEmailAddress.toLowerCase()
      fieldValues.emailaddress = isValidEmail(cleanedEmail)
        ? formEmailAddress
        : null
      fieldErrors.emailaddress = !fieldValues.emailaddress
    } else {
      fieldErrors.emailaddress = true
    }

    // Street validation
    if (formStreet.length < 1) {
      fieldValues.street = null
      fieldErrors.street = true
    } else {
      fieldValues.street = formData.street
      fieldErrors.street = false
    }

    // City validation
    if (formCity.length < 1) {
      fieldValues.city = null
      fieldErrors.city = true
    } else {
      fieldValues.city = formData.city
      fieldErrors.city = false
    }

    // State validation
    if (formState.length < 1) {
      fieldValues.state = null
      fieldErrors.state = true
    } else {
      fieldValues.state = formData.state
      fieldErrors.state = false
    }

    // Zipcode validation
    fieldValues.zipcode = isValidZipcode(formZipCode) ? formZipCode : null
    fieldErrors.zipcode = !isValidZipcode(formZipCode)

    // Phone validation
    const isValidPhone = isValidPhoneNumber(formPhoneNumber, true)
    fieldErrors.phone = !isValidPhone
    if (isValidPhone) {
      fieldValues.phone = formatPhoneNumber(formPhoneNumber)
    } else {
      fieldValues.phone = null
    }

    setFormData(fieldValues)
    setErrorFields(fieldErrors)

    return false
  }

  const setEmailErrorMessage = (
    wrongEmail: boolean,
    message: CCPAFormErrorMessages
  ): string => {
    return wrongEmail
      ? message.errorEmailaddressWrongFormat
      : message.errorEmailaddress
  }

  const successMessage = (
    <>
      <h3 className="u-paddingBottom16">
        {serviceMessages?.serviceSuccessThankyou}
      </h3>
      <p
        className="u-paddingTop16 u-paddingBottom16"
        dangerouslySetInnerHTML={{
          __html: xss(serviceMessages?.serviceSuccess ?? '')
        }}
      />
    </>
  )

  return (
    <div className="u-marginTop32 u-adel-reset">
      {showSuccessMessage && serviceMessages?.serviceSuccess && (
        <Notification
          type={NotificationType.CONFIRM}
          dismissable={false}
          className="CCPAFormNotification u-sizeConstrained"
          children={successMessage}
        />
      )}
      {!showSuccessMessage && (
        <div className="u-sizeConstrained">
          <section
            className="u-marginBottom24 u-size5of12 u-md-size8of12 u-sm-sizeFull Form"
            onBlur={handleFieldChange}
            onKeyUp={handleFieldChange}
            data-form-name={name}
          >
            {formHeading?.title && (
              <h2 className="u-marginBottom32">{formHeading.title}</h2>
            )}
            <div>
              <RadioGroup
                legend="Are you a California resident?"
                error={errorFields.resident}
                message={
                  formErrorMessages.errorResident || 'Please select one option.'
                }
                options={['Yes', 'No']}
                name="resident"
                onOptionSelected={handleOptionSelected}
                testId={testId ? `${testId}-resident` : undefined}
              />
              {formData.resident?.toLowerCase() === 'no' && (
                <p className="Form-help">
                  This right applies only to CA residents
                </p>
              )}
            </div>
            <div className="u-marginTop32">
              <CheckboxList
                legend="Please select the relationship(s) you have with us."
                options={relationshipData}
                onOptionsSelected={handleRelationshipChange}
                error={errorFields.relationship}
                message={formErrorMessages.errorRelationship}
                testId={testId ? `${testId}-relationship` : undefined}
              />
            </div>
            <div className="u-marginTop32">
              <CheckboxList
                legend="Select the request type(s)."
                options={requestTypeData}
                onOptionsSelected={handleRequestTypeChange}
                error={errorFields.requesttype}
                message={formErrorMessages.errorRequesttype}
                testId={testId ? `${testId}-requesttype` : undefined}
              />
            </div>
            <div className="u-marginTop32" />
            <Input
              name="firstname"
              id="firstname"
              placeholder=""
              label={formLabels.firstname}
              reference={firstnameRef}
              error={errorFields.firstname}
              message={formErrorMessages.errorFirstname}
              testId={testId ? `${testId}-firstname` : undefined}
            />
            <Input
              name="lastname"
              id="lastname"
              placeholder=""
              label={formLabels.lastname}
              reference={lastnameRef}
              error={errorFields.lastname}
              message={formErrorMessages.errorLastname}
              testId={testId ? `${testId}-lastname` : undefined}
            />
            <Input
              name="emailaddress"
              id="email"
              placeholder=""
              label={formLabels.emailaddress}
              reference={emailaddressRef}
              error={errorFields.emailaddress}
              message={setEmailErrorMessage(
                errorFields.emailWrongFormat,
                formErrorMessages
              )}
              testId={testId ? `${testId}-email` : undefined}
            />
            <Input
              name="phone"
              id="phone"
              placeholder=""
              label={formLabels.phone}
              reference={phoneRef}
              error={errorFields.phone}
              message={formErrorMessages.errorPhone}
              testId={testId ? `${testId}-phone` : undefined}
            />
            <Input
              name="street"
              id="street"
              placeholder=""
              label={formLabels.street}
              reference={streetRef}
              error={errorFields.street}
              message={formErrorMessages.errorStreet}
              testId={testId ? `${testId}-street` : undefined}
            />
            <Input
              name="city"
              id="city"
              placeholder=""
              label={formLabels.city}
              reference={cityRef}
              error={errorFields.city}
              message={formErrorMessages.errorCity}
              testId={testId ? `${testId}-city` : undefined}
            />
            <Dropdown
              name="state"
              id="state"
              placeholder="Select State"
              label={formLabels.state}
              reference={stateRef}
              callback={handleSelectState}
              data={StatesData}
              className="u-marginBottom20"
            />
            <Input
              name="zipcode"
              id="zipcode"
              placeholder=""
              label={formLabels.zipcode}
              reference={zipcodeRef}
              error={errorFields.zipcode}
              message={formErrorMessages.errorZipcode}
              testId={testId ? `${testId}-zipcode` : undefined}
            />
            <div>
              <RadioGroup
                legend="If you are a client, are you registered on the Secure Site? (optional)"
                options={['Yes', 'No']}
                name="secureSite"
                onOptionSelected={handleOptionalRadio}
                testId={testId ? `${testId}-securesite` : undefined}
              />
            </div>
            <div className="u-marginTop32" />
            {formLabels.accountid && (
              <Input
                name="accountid"
                id="accountid"
                placeholder=""
                label={formLabels.accountid}
                testId={testId ? `${testId}-accountid` : undefined}
              />
            )}
            <div className="u-marginTop32" />
            {formLabels.employeeid && (
              <Input
                name="employeeid"
                id="employeeid"
                placeholder=""
                label={formLabels.employeeid}
                testId={testId ? `${testId}-employeeid` : undefined}
              />
            )}
            <div className="u-marginBottom32" />
            {showServiceFailedMessage && serviceMessages?.serviceFailed && (
              <Notification
                type={NotificationType.ALERT}
                message={serviceMessages.serviceFailed}
                dismissable={false}
                className="u-marginBottom24"
              />
            )}
            <Button
              onClick={submitData}
              isLoading={loading}
              data-analytics={'Button'}
              data-tracking={'Button'}
              children={
                loading ? formLabels.buttonLoadingLabel : formLabels.buttonlabel
              }
            />
          </section>
          {formPleaseNote?.message && (
            <Content className={'u-marginBottom24'}>
              <div
                className="Content"
                dangerouslySetInnerHTML={{
                  __html: xss(formPleaseNote.message)
                }}
              />
            </Content>
          )}
        </div>
      )}
    </div>
  )
}

export default ContactFormCCPA
